import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Switch,
  FormControlLabel,
  Divider,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SmartToy as BotIcon,
  Business as BusinessIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from '../../utils/axiosConfig';
import { widgetUrlForBot } from '../../utils/publicUrls';

const Bots = () => {
  const [bots, setBots] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [models, setModels] = useState([]);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Dialog States
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [iframeDialogOpen, setIframeDialogOpen] = useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [selectedBot, setSelectedBot] = useState(null);
  
  // Form States
  const [botForm, setBotForm] = useState({
    name: '',
    description: '',
    company_id: '',
    llm_provider: 'openrouter',
    model_id: '',
    system_prompt: 'Du bist ein hilfsreicher KI-Assistent.',
    is_active: true,
    widget_color: '#FFD700',
    widget_position: 'bottom-right',
    widget_theme: 'default',
  });

  useEffect(() => {
    loadBots();
    loadCompanies();
    loadAllModels();
  }, []);

  const loadBots = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/bots/`);
      setBots(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Bots:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Bots';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Firmen:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Firmen';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const loadModels = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/models/`);
      setModels(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Modelle:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Modelle';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const loadAllModels = async () => {
    try {
      // Lade Standard-Modelle
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/models/`);
      const standardModels = response.data;
      
      // Lade OpenRouter-Modelle
      const apiKey = localStorage.getItem('openrouter_api_key');
      let openrouterModels = [];
      
      if (apiKey) {
        try {
          const openrouterResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/openrouter/models`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ api_key: apiKey })
          });
          
          if (openrouterResponse.ok) {
            const data = await openrouterResponse.json();
            if (data.success && data.models) {
              openrouterModels = data.models.map(model => ({
                id: model.id,
                name: model.name || model.id,
                provider: model.id.split('/')[0] || 'Unknown',
                model_name: model.id,
                description: model.description || 'Keine Beschreibung verfügbar',
                temperature: 0.7,
                max_tokens: model.context_length || 2000
              }));
            }
          }
        } catch (openrouterError) {
          console.warn('OpenRouter-Modelle konnten nicht geladen werden:', openrouterError);
        }
      }
      
      // Kombiniere beide Modell-Listen
      const allModels = [...standardModels, ...openrouterModels];
      setModels(allModels);
      setOpenrouterModels(openrouterModels);
      
    } catch (error) {
      console.error('Fehler beim Laden der Modelle:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Modelle';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const loadOpenrouterModels = async () => {
    try {
      const apiKey = localStorage.getItem('openrouter_api_key');
      if (!apiKey) return;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/openrouter/models`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.models) {
          const formattedModels = data.models.map(model => ({
            id: model.id,
            name: model.name || model.id,
            provider: model.id.split('/')[0] || 'Unknown',
            description: model.description || 'Keine Beschreibung verfügbar',
            cost: 'Siehe OpenRouter Pricing',
            maxTokens: model.context_length || 0
          }));
          setOpenrouterModels(formattedModels);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der OpenRouter Modelle:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der OpenRouter Modelle';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleCreateBot = async () => {
    // Form-Validierung
    if (!botForm.name.trim()) {
      setSnackbar({ open: true, message: 'Bot-Name ist erforderlich', severity: 'error' });
      return;
    }
    if (!botForm.company_id) {
      setSnackbar({ open: true, message: 'Firma ist erforderlich', severity: 'error' });
      return;
    }
    if (!botForm.model_id || botForm.model_id === '') {
      setSnackbar({ open: true, message: 'Modell ist erforderlich', severity: 'error' });
      return;
    }

    try {
      setSaving(true);
      // Konvertiere String-IDs zu Integer mit Validierung
      const companyId = parseInt(botForm.company_id);
      
      if (isNaN(companyId)) {
        setSnackbar({ open: true, message: 'Ungültige Firma-ID', severity: 'error' });
        return;
      }
      
      // Prüfe, ob es ein OpenRouter-Modell ist (String-ID) oder Standard-Modell (Integer-ID)
      let modelId = botForm.model_id;
      if (typeof modelId === 'string' && !isNaN(parseInt(modelId))) {
        // Standard-Modell mit Integer-ID
        modelId = parseInt(modelId);
      } else if (typeof modelId === 'string' && isNaN(parseInt(modelId))) {
        // OpenRouter-Modell mit String-ID - behalte als String
        modelId = modelId;
      } else {
        setSnackbar({ open: true, message: 'Ungültige Modell-ID', severity: 'error' });
        return;
      }
      
      const botData = {
        ...botForm,
        company_id: companyId,
        model_id: modelId
      };
      
      console.log('Sending bot data:', botData); // Debug-Log
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/bots/`, botData);
      setBots(prev => [response.data, ...prev]);
      setCreateDialogOpen(false);
      
      // URL-Popup anzeigen
      const widgetUrl = widgetUrlForBot(response.data.id);
      setGeneratedUrl(widgetUrl);
      setUrlDialogOpen(true);
      
      resetForm();
      setSnackbar({ open: true, message: 'Bot erfolgreich erstellt!', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Erstellen des Bots:', error);
      console.error('Error response:', error.response?.data); // Debug-Log
      console.error('Error status:', error.response?.status); // Debug-Log
      console.error('Error headers:', error.response?.headers); // Debug-Log
      
      let errorMessage = 'Fehler beim Erstellen des Bots';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg || err).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBot = async () => {
    // Form-Validierung
    if (!botForm.name.trim()) {
      setSnackbar({ open: true, message: 'Bot-Name ist erforderlich', severity: 'error' });
      return;
    }
    if (!botForm.company_id) {
      setSnackbar({ open: true, message: 'Firma ist erforderlich', severity: 'error' });
      return;
    }
    if (!botForm.model_id || botForm.model_id === '') {
      setSnackbar({ open: true, message: 'Modell ist erforderlich', severity: 'error' });
      return;
    }

    try {
      setSaving(true);
      // Konvertiere String-IDs zu Integer mit Validierung
      const companyId = parseInt(botForm.company_id);
      
      if (isNaN(companyId)) {
        setSnackbar({ open: true, message: 'Ungültige Firma-ID', severity: 'error' });
        return;
      }
      
      // Prüfe, ob es ein OpenRouter-Modell ist (String-ID) oder Standard-Modell (Integer-ID)
      let modelId = botForm.model_id;
      if (typeof modelId === 'string' && !isNaN(parseInt(modelId))) {
        // Standard-Modell mit Integer-ID
        modelId = parseInt(modelId);
      } else if (typeof modelId === 'string' && isNaN(parseInt(modelId))) {
        // OpenRouter-Modell mit String-ID - behalte als String
        modelId = modelId;
      } else {
        setSnackbar({ open: true, message: 'Ungültige Modell-ID', severity: 'error' });
        return;
      }
      
      const botData = {
        ...botForm,
        company_id: companyId,
        model_id: modelId
      };
      
      console.log('Updating bot data:', botData); // Debug-Log
      
      const response = await axios.put(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/bots/${selectedBot.id}`, botData);
      setBots(prev => prev.map(bot => bot.id === selectedBot.id ? response.data : bot));
      setEditDialogOpen(false);
      setSelectedBot(null);
      resetForm();
      setSnackbar({ open: true, message: 'Bot erfolgreich aktualisiert!', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Bots:', error);
      console.error('Error response:', error.response?.data); // Debug-Log
      console.error('Error status:', error.response?.status); // Debug-Log
      console.error('Error headers:', error.response?.headers); // Debug-Log
      
      let errorMessage = 'Fehler beim Aktualisieren des Bots';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg || err).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBot = async (botId) => {
    if (window.confirm('Möchten Sie diesen Bot wirklich löschen?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/bots/${botId}`);
        setBots(prev => prev.filter(bot => bot.id !== botId));
        setSnackbar({ open: true, message: 'Bot erfolgreich gelöscht!', severity: 'success' });
      } catch (error) {
        console.error('Fehler beim Löschen des Bots:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Löschen des Bots';
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      }
    }
  };

  const handleToggleBot = async (bot) => {
    try {
      const updatedBot = { ...bot, is_active: !bot.is_active };
      const response = await axios.put(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/bots/${bot.id}`, updatedBot);
      setBots(prev => prev.map(b => b.id === bot.id ? response.data : b));
      setSnackbar({ 
        open: true, 
        message: `Bot ${updatedBot.is_active ? 'aktiviert' : 'deaktiviert'}!`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Fehler beim Umschalten des Bot-Status:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Umschalten des Bot-Status';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleEditBot = (bot) => {
    setSelectedBot(bot);
    setBotForm({
      name: bot.name,
      description: bot.description,
      company_id: bot.company_id,
      llm_provider: bot.llm_provider || 'openrouter',
      model_id: bot.model_id,
      system_prompt: bot.system_prompt,
      is_active: bot.is_active,
      widget_color: bot.widget_color || '#FFD700',
      widget_position: bot.widget_position || 'bottom-right',
      widget_theme: bot.widget_theme || 'default',
    });
    setEditDialogOpen(true);
  };

  const handleShowIframe = (bot) => {
    setSelectedBot(bot);
    setIframeDialogOpen(true);
  };

  const resetForm = () => {
    setBotForm({
      name: '',
      description: '',
      company_id: '',
      llm_provider: 'openrouter',
      model_id: '',
      system_prompt: 'Du bist ein hilfsreicher KI-Assistent.',
      is_active: true,
      widget_color: '#FFD700',
      widget_position: 'bottom-right',
      widget_theme: 'default',
    });
  };

  const generateIframeCode = (bot) => {
    // Verwende die korrekte Widget-URL basierend auf Bot-ID
    const iframeUrl = widgetUrlForBot(bot.id);
    return `<iframe 
  src="${iframeUrl}" 
  width="350" 
  height="500" 
  frameborder="0" 
  style="border-radius: 10px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
</iframe>`;
  };

  const generateWidgetCode = (bot) => {
    // Verwende die korrekte Widget-URL basierend auf Bot-ID
    const widgetUrl = widgetUrlForBot(bot.id);
    return `<!-- KiChatbot Widget für ${bot.name} -->
<div id="kichatbot-widget" style="position: fixed; ${bot.widget_position === 'bottom-right' ? 'bottom: 20px; right: 20px;' : 'bottom: 20px; left: 20px;'} z-index: 9999;">
  <div id="kichatbot-toggle" style="width: 60px; height: 60px; background: ${bot.widget_color}; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
    <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
    </svg>
  </div>
  <div id="kichatbot-chat" style="display: none; width: 350px; height: 500px; background: white; border-radius: 10px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); margin-bottom: 10px;">
    <iframe src="${widgetUrl}" width="100%" height="100%" frameborder="0" style="border-radius: 10px;"></iframe>
  </div>
</div>

<script>
document.addEventListener('DOMContentLoaded', function() {
  const toggle = document.getElementById('kichatbot-toggle');
  const chat = document.getElementById('kichatbot-chat');
  
  toggle.addEventListener('click', function() {
    chat.style.display = chat.style.display === 'none' ? 'block' : 'none';
  });
});
</script>`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({ open: true, message: 'Code in Zwischenablage kopiert!', severity: 'success' });
    });
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unbekannt';
  };

  const getModelName = (modelId) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.name : 'Unbekannt';
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
          Bot Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Erstellen und verwalten Sie Chatbot-Instanzen für verschiedene Firmen
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #F57F17 0%, #FFD700 100%)',
            }
          }}
        >
          Neuer Bot
        </Button>
      </Box>

      {/* Bots Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon />
            Alle Bots ({bots.length})
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Bot</TableCell>
                    <TableCell>Firma</TableCell>
                    <TableCell>Modell</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Erstellt</TableCell>
                    <TableCell>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bots.map((bot) => (
                    <TableRow key={bot.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <BotIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {bot.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {bot.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getCompanyName(bot.company_id)} 
                          color="primary" 
                          variant="outlined"
                          icon={<BusinessIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getModelName(bot.model_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Switch
                            checked={bot.is_active}
                            onChange={() => handleToggleBot(bot)}
                            color="primary"
                          />
                          <Chip
                            label={bot.is_active ? 'Aktiv' : 'Inaktiv'}
                            color={bot.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {bot.created_at ? format(new Date(bot.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : 'Unbekannt'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Tooltip title="Widget direkt testen">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => window.open(widgetUrlForBot(bot.id), '_blank')}
                              startIcon={<OpenIcon />}
                              sx={{ minWidth: 'auto', px: 1 }}
                            >
                              Testen
                            </Button>
                          </Tooltip>
                          <Tooltip title="Iframe Code anzeigen">
                            <IconButton
                              onClick={() => handleShowIframe(bot)}
                              size="small"
                              color="primary"
                            >
                              <CodeIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Bot bearbeiten">
                            <IconButton
                              onClick={() => handleEditBot(bot)}
                              size="small"
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Bot löschen">
                            <IconButton
                              onClick={() => handleDeleteBot(bot.id)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Bot Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon />
            Neuen Bot erstellen
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bot Name"
                value={botForm.name}
                onChange={(e) => setBotForm({ ...botForm, name: e.target.value })}
                placeholder="z.B. Kundenservice Bot"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Firma auswählen</InputLabel>
                <Select
                  value={botForm.company_id}
                  onChange={(e) => setBotForm({ ...botForm, company_id: e.target.value })}
                  label="Firma auswählen"
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon />
                        {company.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={botForm.description}
                onChange={(e) => setBotForm({ ...botForm, description: e.target.value })}
                placeholder="Kurze Beschreibung des Bots"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>LLM-Anbieter</InputLabel>
                <Select
                  value={botForm.llm_provider || 'openrouter'}
                  label="LLM-Anbieter"
                  onChange={(e) => setBotForm({ ...botForm, llm_provider: e.target.value })}
                >
                  <MenuItem value="openrouter">OpenRouter (viele Modelle, ein Key)</MenuItem>
                  <MenuItem value="openai">OpenAI (direkt, z. B. gpt-4o)</MenuItem>
                  <MenuItem value="anthropic">Anthropic Claude (direkt)</MenuItem>
                </Select>
              </FormControl>
              {(botForm.llm_provider || 'openrouter') === 'openrouter' ? (
              <FormControl fullWidth required>
                <InputLabel>KI-Modell auswählen</InputLabel>
                <Select
                  value={botForm.model_id}
                  onChange={(e) => setBotForm({ ...botForm, model_id: e.target.value })}
                  label="KI-Modell auswählen"
                >
                  {models.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BotIcon />
                        {model.name}
                      </Box>
                    </MenuItem>
                  ))}
                  {openrouterModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <BotIcon />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {model.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {model.provider} • {model.description}
                          </Typography>
                        </Box>
                        <Chip
                          label={model.cost}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              ) : (
              <TextField
                fullWidth
                required
                label="Modell-ID"
                value={botForm.model_id}
                onChange={(e) => setBotForm({ ...botForm, model_id: e.target.value })}
                placeholder={botForm.llm_provider === 'openai' ? 'gpt-4o' : 'claude-sonnet-4-20250514'}
                helperText={
                  botForm.llm_provider === 'openai'
                    ? 'OpenAI: z. B. gpt-4o, gpt-4-turbo, o1 (API-Key in globalen Einstellungen oder Bot-Feld)'
                    : 'Anthropic: z. B. claude-sonnet-4-20250514 (API-Key in globalen Einstellungen oder Bot-Feld)'
                }
              />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Widget Farbe"
                type="color"
                value={botForm.widget_color}
                onChange={(e) => setBotForm({ ...botForm, widget_color: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Widget-Theme</InputLabel>
                <Select
                  value={botForm.widget_theme || 'default'}
                  label="Widget-Theme"
                  onChange={(e) => setBotForm({ ...botForm, widget_theme: e.target.value })}
                >
                  <MenuItem value="default">Standard</MenuItem>
                  <MenuItem value="compact">Kompakt</MenuItem>
                  <MenuItem value="minimal">Minimal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Widget-Position</InputLabel>
                <Select
                  value={botForm.widget_position || 'bottom-right'}
                  label="Widget-Position"
                  onChange={(e) => setBotForm({ ...botForm, widget_position: e.target.value })}
                >
                  <MenuItem value="bottom-right">Unten rechts</MenuItem>
                  <MenuItem value="bottom-left">Unten links</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="System Prompt"
                value={botForm.system_prompt}
                onChange={(e) => setBotForm({ ...botForm, system_prompt: e.target.value })}
                placeholder="Definieren Sie, wie sich der Bot verhalten soll..."
                helperText="Dieser Text definiert die Persönlichkeit und das Verhalten des Chatbots"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={botForm.is_active}
                    onChange={(e) => setBotForm({ ...botForm, is_active: e.target.checked })}
                  />
                }
                label="Bot aktivieren"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateBot}
            variant="contained"
            disabled={saving || !botForm.name || !botForm.company_id || !botForm.model_id}
            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {saving ? 'Erstelle...' : 'Bot erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Bot Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            Bot bearbeiten
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bot Name"
                value={botForm.name}
                onChange={(e) => setBotForm({ ...botForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Firma auswählen</InputLabel>
                <Select
                  value={botForm.company_id}
                  onChange={(e) => setBotForm({ ...botForm, company_id: e.target.value })}
                  label="Firma auswählen"
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon />
                        {company.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={botForm.description}
                onChange={(e) => setBotForm({ ...botForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>LLM-Anbieter</InputLabel>
                <Select
                  value={botForm.llm_provider || 'openrouter'}
                  label="LLM-Anbieter"
                  onChange={(e) => setBotForm({ ...botForm, llm_provider: e.target.value })}
                >
                  <MenuItem value="openrouter">OpenRouter</MenuItem>
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic Claude</MenuItem>
                </Select>
              </FormControl>
              {(botForm.llm_provider || 'openrouter') === 'openrouter' ? (
              <FormControl fullWidth required>
                <InputLabel>KI-Modell auswählen</InputLabel>
                <Select
                  value={botForm.model_id}
                  onChange={(e) => setBotForm({ ...botForm, model_id: e.target.value })}
                  label="KI-Modell auswählen"
                >
                  {models.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BotIcon />
                        {model.name}
                      </Box>
                    </MenuItem>
                  ))}
                  {openrouterModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BotIcon />
                        {model.name || model.id}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              ) : (
              <TextField
                fullWidth
                required
                label="Modell-ID"
                value={botForm.model_id}
                onChange={(e) => setBotForm({ ...botForm, model_id: e.target.value })}
                placeholder={botForm.llm_provider === 'openai' ? 'gpt-4o' : 'claude-sonnet-4-20250514'}
                helperText="API-Key unter globale Einstellungen oder im Bot-Feld (openrouter_api_key) hinterlegen"
              />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Widget Farbe"
                type="color"
                value={botForm.widget_color}
                onChange={(e) => setBotForm({ ...botForm, widget_color: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Widget-Theme</InputLabel>
                <Select
                  value={botForm.widget_theme || 'default'}
                  label="Widget-Theme"
                  onChange={(e) => setBotForm({ ...botForm, widget_theme: e.target.value })}
                >
                  <MenuItem value="default">Standard</MenuItem>
                  <MenuItem value="compact">Kompakt</MenuItem>
                  <MenuItem value="minimal">Minimal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Widget-Position</InputLabel>
                <Select
                  value={botForm.widget_position || 'bottom-right'}
                  label="Widget-Position"
                  onChange={(e) => setBotForm({ ...botForm, widget_position: e.target.value })}
                >
                  <MenuItem value="bottom-right">Unten rechts</MenuItem>
                  <MenuItem value="bottom-left">Unten links</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="System Prompt"
                value={botForm.system_prompt}
                onChange={(e) => setBotForm({ ...botForm, system_prompt: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={botForm.is_active}
                    onChange={(e) => setBotForm({ ...botForm, is_active: e.target.checked })}
                  />
                }
                label="Bot aktivieren"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleUpdateBot}
            variant="contained"
            disabled={saving || !botForm.name || !botForm.company_id || !botForm.model_id}
            startIcon={saving ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {saving ? 'Speichere...' : 'Bot aktualisieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Iframe Code Dialog */}
      <Dialog
        open={iframeDialogOpen}
        onClose={() => setIframeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon />
            Iframe Code für {selectedBot?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Einfacher Iframe Code:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={selectedBot ? generateIframeCode(selectedBot) : ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={() => copyToClipboard(generateIframeCode(selectedBot))}>
                  <CopyIcon />
                </IconButton>
              )
            }}
            sx={{ mb: 3 }}
          />
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" sx={{ mb: 2 }}>
            Erweitertes Widget mit Toggle-Button:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={selectedBot ? generateWidgetCode(selectedBot) : ''}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={() => copyToClipboard(generateWidgetCode(selectedBot))}>
                  <CopyIcon />
                </IconButton>
              )
            }}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Hinweis:</strong> Der erweiterte Widget-Code erstellt einen schwebenden Button 
              in der unteren rechten Ecke Ihrer Website. Klicken Sie darauf, um den Chat zu öffnen/schließen.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIframeDialogOpen(false)}>
            Schließen
          </Button>
          <Button
            onClick={() => window.open(widgetUrlForBot(selectedBot?.id), '_blank')}
            variant="contained"
            startIcon={<OpenIcon />}
          >
            Widget testen
          </Button>
        </DialogActions>
      </Dialog>

      {/* URL Popup Dialog */}
      <Dialog
        open={urlDialogOpen}
        onClose={() => setUrlDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            Bot erfolgreich erstellt!
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Ihr Bot ist jetzt verfügbar. Hier ist der Widget-Link:
          </Typography>
          
          <TextField
            fullWidth
            value={generatedUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={() => copyToClipboard(generatedUrl)}>
                  <CopyIcon />
                </IconButton>
              )
            }}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<OpenIcon />}
              onClick={() => window.open(generatedUrl, '_blank')}
            >
              Widget testen
            </Button>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => copyToClipboard(generatedUrl)}
            >
              Link kopieren
            </Button>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Tipp:</strong> Sie können diesen Link direkt in Ihre Website einbetten oder 
              den erweiterten Widget-Code über den "Code anzeigen" Button generieren.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUrlDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default Bots;